<script setup lang="ts">
  import { ref } from 'vue';
  import { PillColor, PillAppearance } from './Pill.types';
  import { Icon, type IconName, Typo } from '@/components';

  interface PillProps {
    appearance?: `${PillAppearance}`;
    color?: `${PillColor}`;
    iconLeft?: IconName;
    iconRight?: IconName;
  }

  const props = withDefaults(defineProps<PillProps>(), {
    appearance: PillAppearance.Primary,
    color: PillColor.Sky,
    iconLeft: undefined,
    iconRight: undefined,
  });

  const typoElement = ref<typeof Typo | null>(null);
</script>

<template>
  <span :class="['tl-pill', `tl-pill_${props.appearance}`, `tl-pill_color-${props.color}`]">
    <slot name="prepend" />
    <Icon v-if="iconLeft" class="tl-pill__icon" :name="iconLeft" size="s" />

    <Typo ref="typoElement" class="tl-pill__typo" variant="body-m-400">
      <slot />
    </Typo>

    <Icon v-if="iconRight" class="tl-pill__icon" :name="iconRight" size="s" />
    <slot name="append" />
  </span>
</template>

<style lang="scss" scoped>
  .tl-pill {
    $root: &;

    align-items: center;
    border-radius: var(--rounding-s);
    box-sizing: border-box;
    color: var(--color-text-inverse);
    display: inline-flex;
    gap: var(--spacing-xxs);
    height: 24px;
    max-width: 100%;
    padding: 0 var(--spacing-xs);
    vertical-align: bottom;

    &_secondary {
      border: 1px solid var(--color-text-inverse);
    }

    &_color {
      &-black {
        &#{$root}_primary {
          background-color: var(--color-neutral-80);
        }

        &#{$root}_secondary {
          background-color: var(--color-neutral-05);
          border-color: var(--color-neutral-40);
          color: var(--color-text-primary);

          #{$root}__icon {
            fill: var(--color-text-primary);
          }
        }
      }

      &-slate {
        &#{$root}_primary {
          background-color: var(--color-slate-40);
        }

        &#{$root}_secondary {
          background-color: var(--color-slate-10);
          border-color: var(--color-slate-30);
          color: var(--color-slate-40);

          #{$root}__icon {
            fill: var(--color-slate-40);
          }
        }
      }

      &-blue {
        &#{$root}_primary {
          background-color: var(--color-blue-30);
        }

        &#{$root}_secondary {
          background-color: var(--color-blue-05);
          border-color: var(--color-blue-30);
          color: var(--color-blue-40);

          #{$root}__icon {
            fill: var(--color-blue-40);
          }
        }
      }

      &-sky {
        &#{$root}_primary {
          background-color: var(--color-sky-40);
        }

        &#{$root}_secondary {
          background-color: var(--color-sky-05);
          border-color: var(--color-sky-40);
          color: var(--color-sky-50);

          #{$root}__icon {
            fill: var(--color-sky-50);
          }
        }
      }

      &-cyan {
        &#{$root}_primary {
          background-color: var(--color-cyan-50);
        }

        &#{$root}_secondary {
          background-color: var(--color-cyan-05);
          border-color: var(--color-cyan-40);
          color: var(--color-cyan-50);

          #{$root}__icon {
            fill: var(--color-cyan-50);
          }
        }
      }

      &-teal {
        &#{$root}_primary {
          background-color: var(--color-teal-40);
        }

        &#{$root}_secondary {
          background-color: var(--color-teal-05);
          border-color: var(--color-teal-40);
          color: var(--color-teal-50);

          #{$root}__icon {
            fill: var(--color-teal-50);
          }
        }
      }

      &-green {
        &#{$root}_primary {
          background-color: var(--color-green-50);
        }

        &#{$root}_secondary {
          background-color: var(--color-green-05);
          border-color: var(--color-green-40);
          color: var(--color-green-50);

          #{$root}__icon {
            fill: var(--color-green-50);
          }
        }
      }

      &-lime {
        &#{$root}_primary {
          background-color: var(--color-lime-50);
        }

        &#{$root}_secondary {
          background-color: var(--color-lime-05);
          border-color: var(--color-lime-40);
          color: var(--color-lime-50);

          #{$root}__icon {
            fill: var(--color-lime-50);
          }
        }
      }

      &-yellow {
        &#{$root}_primary {
          background-color: var(--color-yellow-40);
        }

        &#{$root}_secondary {
          background-color: var(--color-yellow-05);
          border-color: var(--color-yellow-40);
          color: var(--color-yellow-50);

          #{$root}__icon {
            fill: var(--color-yellow-50);
          }
        }
      }

      &-orange {
        &#{$root}_primary {
          background-color: var(--color-orange-40);
        }

        &#{$root}_secondary {
          background-color: var(--color-orange-05);
          border-color: var(--color-orange-40);
          color: var(--color-orange-70);

          #{$root}__icon {
            fill: var(--color-orange-70);
          }
        }
      }

      &-red {
        &#{$root}_primary {
          background-color: var(--color-red-40);
        }

        &#{$root}_secondary {
          background-color: var(--color-red-05);
          border-color: var(--color-red-30);
          color: var(--color-red-40);

          #{$root}__icon {
            fill: var(--color-red-40);
          }
        }
      }

      &-rose {
        &#{$root}_primary {
          background-color: var(--color-rose-40);
        }

        &#{$root}_secondary {
          background-color: var(--color-rose-05);
          border-color: var(--color-rose-30);
          color: var(--color-rose-40);

          #{$root}__icon {
            fill: var(--color-rose-40);
          }
        }
      }

      &-pink {
        &#{$root}_primary {
          background-color: var(--color-pink-30);
        }

        &#{$root}_secondary {
          background-color: var(--color-pink-05);
          border-color: var(--color-pink-30);
          color: var(--color-pink-40);

          #{$root}__icon {
            fill: var(--color-pink-40);
          }
        }
      }

      &-purple {
        &#{$root}_primary {
          background-color: var(--color-purple-30);
        }

        &#{$root}_secondary {
          background-color: var(--color-purple-05);
          border-color: var(--color-purple-30);
          color: var(--color-purple-40);

          #{$root}__icon {
            fill: var(--color-purple-40);
          }
        }
      }

      &-indigo {
        &#{$root}_primary {
          background-color: var(--color-indigo-30);
        }

        &#{$root}_secondary {
          background-color: var(--color-indigo-05);
          border-color: var(--color-indigo-30);
          color: var(--color-indigo-40);

          #{$root}__icon {
            fill: var(--color-indigo-40);
          }
        }
      }

      &-gray {
        &#{$root}_primary {
          background-color: var(--color-text-inverse);
          border: 1px solid var(--color-slate-20);
          color: var(--color-text-primary);

          #{$root}__icon {
            fill: var(--color-text-primary);
          }
        }

        &#{$root}_secondary {
          background-color: var(--color-text-inverse);
          border-color: var(--color-slate-30);
          color: var(--color-slate-40);

          #{$root}__icon {
            fill: var(--color-slate-40);
          }
        }
      }
    }

    &__icon {
      fill: var(--color-text-inverse);
      flex-shrink: 0;
    }

    &__typo {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
</style>
